import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Eye, EyeOff } from "lucide-react";

const GoogleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M47.532 24.552c0-1.638-.147-3.209-.42-4.712H24v8.915h13.3c-.574 3.1-2.318 5.725-4.943 7.49v6.213h7.998c4.683-4.31 7.377-10.656 7.377-17.906z"
      fill="#4285F4"
    />
    <path
      d="M24 48c6.66 0 12.245-2.205 16.326-5.992l-7.998-6.213c-2.22 1.49-5.06 2.374-8.328 2.374-6.4 0-11.82-4.318-13.752-10.116H2.03v6.35C6.09 42.84 14.31 48 24 48z"
      fill="#34A853"
    />
    <path
      d="M10.248 28.053A14.358 14.358 0 019.49 24c0-1.41.242-2.775.686-4.053V13.6H2.03A24.02 24.02 0 000 24c0 3.86.93 7.5 2.57 10.4l7.678-6.347z"
      fill="#FBBC05"
    />
    <path
      d="M24 9.5c3.622 0 6.88 1.246 9.45 3.69l7.088-7.088C36.245 2.328 30.66 0 24 0 14.31 0 6.09 5.16 2.57 13.6l7.606 6.347C12.18 13.818 17.6 9.5 24 9.5z"
      fill="#EA4335"
    />
  </svg>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, googleSignIn, loading: authLoading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const { user, error: err } = await signIn(email, password);

      if (err) {
        setError(err.message || "Unable to login");
      } else {
        // ✅ Check for pending order
        const pendingOrder = localStorage.getItem("pending_order");

        if (pendingOrder) {
          // Redirect to make-payment page to continue order
          navigate("/confirm-installation", { replace: true });
        } else {
          // Normal role-based redirect
          const role = user?.user_metadata?.role;
          if (role === "installer") navigate("/installer", { replace: true });
          else navigate("/dashboard", { replace: true });
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    try {
      setLoading(true);
      await googleSignIn();
    } catch (e) {
      setError(e.message || "Google sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      {/* Top nav */}
      <div className="col-span-1 md:col-span-2">
        <div className="flex items-center justify-between px-6 md:px-10 py-4">
          {/* Brand */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
            data-testid="auth-topbrand"
          >
            <span className="inline-flex h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600" />
            <span className="font-extrabold text-slate-900 tracking-tight">
              RoofBidder
            </span>
          </button>
          <div className="flex items-center gap-6 text-sm">
            <a
              href="mailto:support@roofbidder.com"
              className="text-slate-600 hover:text-slate-900"
              data-testid="auth-contact-link"
            >
              Contact us
            </a>
            <Link
              to="/signup"
              className="text-blue-600 font-semibold"
              data-testid="auth-register-link"
            >
              Register
            </Link>
          </div>
        </div>
      </div>

      {/* Left brand/gradient panel */}
      <div className="hidden md:block bg-[linear-gradient(135deg,#eef2ff_0%,#f5f7ff_35%,#e9eefc_70%,#eef2ff_100%)]" />

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-6 md:px-12">
        <div className="w-full max-w-sm" data-testid="login-page">
          <h1
            className="text-[32px] leading-tight font-extrabold tracking-tight text-slate-900 mb-8"
            data-testid="login-title"
          >
            Log in to RoofBidder
          </h1>

          {error && (
            <div
              className="mb-5 bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-xl"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          {/* Google login */}
          <button
            onClick={handleGoogle}
            disabled={loading || authLoading}
            data-testid="login-google-button"
            className="w-full h-12 inline-flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition"
          >
            <GoogleIcon />
            <span className="font-medium">Login with Google</span>
          </button>

          {/* Divider */}
          <div className="relative my-6" aria-hidden>
            <div className="h-[1px] bg-slate-200" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="px-2 text-[10px] uppercase tracking-wider bg-white text-slate-400">
                Or, sign in with email
              </span>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="login-form"
          >
            <div>
              <label
                className="block text-sm font-medium text-slate-700 mb-1"
                htmlFor="email"
              >
                Email address
              </label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                className="w-full h-12 rounded-xl border border-slate-200 bg-slate-50 px-3 text-[15px] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium text-slate-700 mb-1"
                htmlFor="password"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full h-12 rounded-xl border border-slate-200 bg-slate-50 px-3 pr-10 text-[15px] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 text-slate-500"
                  aria-label="Toggle password visibility"
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading || authLoading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold disabled:opacity-50 shadow-sm"
            >
              {loading ? "Logging in..." : "Log in"}
            </button>
          </form>

          <p className="mt-6 text-center text-slate-600 text-sm">
            Don’t have an account?{" "}
            <Link
              to="/signup"
              className="text-blue-600 font-semibold"
              data-testid="goto-signup-link"
            >
              Register
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
